import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { unitsByCategory, expressCategories } from '../data/mock';

// Map category IDs to their translation key prefixes
const getUnitTranslationKey = (categoryId) => {
  const keyMap = {
    'length': 'lengthUnits',
    'area': 'areaUnits',
    'weight': 'weightUnits',
    'volume': 'volumeUnits',
    'temperature': 'temperatureUnits',
    'speed': 'speedUnits',
    'time': 'timeUnits'
  };
  return keyMap[categoryId] || `${categoryId}Units`;
};

const UnitFinder = ({ onConvert }) => {
  const { t } = useTranslation();
  const [fromSearch, setFromSearch] = useState('');
  const [toSearch, setToSearch] = useState('');
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [selectedFrom, setSelectedFrom] = useState(null);
  const [selectedTo, setSelectedTo] = useState(null);

  // Get all units flat list
  const getAllUnits = () => {
    const units = [];
    Object.entries(unitsByCategory).forEach(([category, categoryUnits]) => {
      categoryUnits.forEach(unit => {
        units.push({ ...unit, category });
      });
    });
    return units;
  };

  const searchUnits = (query) => {
    if (!query.trim()) return [];
    const allUnits = getAllUnits();
    const lowerQuery = query.toLowerCase();
    return allUnits.filter(unit => 
      unit.name.toLowerCase().includes(lowerQuery) ||
      unit.symbol.toLowerCase().includes(lowerQuery)
    ).slice(0, 8);
  };

  const handleFromSearch = (e) => {
    const value = e.target.value;
    setFromSearch(value);
    setFromSuggestions(searchUnits(value));
    if (!value) setSelectedFrom(null);
  };

  const handleToSearch = (e) => {
    const value = e.target.value;
    setToSearch(value);
    setToSuggestions(searchUnits(value));
    if (!value) setSelectedTo(null);
  };

  const getTranslatedUnitName = (unit) => {
    return t(`${getUnitTranslationKey(unit.category)}.${unit.id}.name`, unit.name);
  };

  const selectFromUnit = (unit) => {
    setSelectedFrom(unit);
    setFromSearch(getTranslatedUnitName(unit));
    setFromSuggestions([]);
  };

  const selectToUnit = (unit) => {
    setSelectedTo(unit);
    setToSearch(getTranslatedUnitName(unit));
    setToSuggestions([]);
  };

  const handleConvert = () => {
    if (selectedFrom && selectedTo && selectedFrom.category === selectedTo.category) {
      onConvert(selectedFrom.category, selectedFrom.id, selectedTo.id);
    }
  };

  const getCategoryLabel = (categoryId) => {
    const cat = expressCategories.find(c => c.id === categoryId);
    return cat ? cat.label : categoryId;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <Search size={20} className="text-[#4a7c9b]" />
        {t('unitFinder.title')}
      </h3>

      <div className="grid md:grid-cols-[1fr,auto,1fr] gap-4 items-start">
        {/* From Unit */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">{t('unitFinder.fromUnit')}</label>
          <div className="relative">
            <input
              type="text"
              value={fromSearch}
              onChange={handleFromSearch}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent"
              placeholder={t('unitFinder.searchPlaceholder')}
            />
            {fromSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {fromSuggestions.map((unit, idx) => (
                  <button
                    key={`${unit.category}-${unit.id}-${idx}`}
                    onClick={() => selectFromUnit(unit)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm border-b border-gray-100 last:border-0"
                  >
                    <span className="font-medium">{t(`${getUnitTranslationKey(unit.category)}.${unit.id}.name`, unit.name)}</span>
                    <span className="text-gray-500 ml-2">({unit.symbol})</span>
                    <span className="text-xs text-gray-400 block">{t(`sidebar.converters.${unit.category}`, getCategoryLabel(unit.category))}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Arrow */}
        <div className="hidden md:flex items-center justify-center pt-8">
          <span className="text-2xl text-gray-400">→</span>
        </div>

        {/* To Unit */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">{t('unitFinder.toUnit')}</label>
          <div className="relative">
            <input
              type="text"
              value={toSearch}
              onChange={handleToSearch}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent"
              placeholder={t('unitFinder.searchPlaceholder')}
            />
            {toSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {toSuggestions.map((unit, idx) => (
                  <button
                    key={`${unit.category}-${unit.id}-${idx}`}
                    onClick={() => selectToUnit(unit)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm border-b border-gray-100 last:border-0"
                  >
                    <span className="font-medium">{t(`${getUnitTranslationKey(unit.category)}.${unit.id}.name`, unit.name)}</span>
                    <span className="text-gray-500 ml-2">({unit.symbol})</span>
                    <span className="text-xs text-gray-400 block">{t(`sidebar.converters.${unit.category}`, getCategoryLabel(unit.category))}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedFrom && selectedTo && (
        <div className="mt-4">
          {selectedFrom.category === selectedTo.category ? (
            <button
              onClick={handleConvert}
              className="px-4 py-2 bg-[#4a7c9b] text-white rounded hover:bg-[#3d6a86] transition-colors"
            >
              {t('unitFinder.convertButton', { from: t(`${getUnitTranslationKey(selectedFrom.category)}.${selectedFrom.id}.name`, selectedFrom.name), to: t(`${getUnitTranslationKey(selectedTo.category)}.${selectedTo.id}.name`, selectedTo.name) })}
            </button>
          ) : (
            <p className="text-sm text-orange-600">
              {t('unitFinder.categoryMismatch', { from: t(`sidebar.converters.${selectedFrom.category}`, getCategoryLabel(selectedFrom.category)), to: t(`sidebar.converters.${selectedTo.category}`, getCategoryLabel(selectedTo.category)) })}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default UnitFinder;
